import React from 'react';
import { Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import Select from 'react-select';
import { getServicesByClient } from '../../Service/Api/api';
import { ListService } from '../../Service/Types/service';
import { Site } from '../Types/type';

interface SelectServiceProps {
  name: string;
  value: any;
  setFieldValue: (field: string, value: any) => void;
  isDisabled?: boolean;
  touched?: any;
  error?: any;
  defaultValue?: string;
  selectedSites?: Site | Site[];
}

const SelectService = ({
  name,
  value,
  setFieldValue,
  isDisabled = false,
  touched,
  error,
  defaultValue,
  selectedSites = [],
}: SelectServiceProps): JSX.Element => {
  const intl = useIntl();

  const { isLoading, data: services } = useQuery<ListService[]>(
    ['get-service-by-client'],
    async () => getServicesByClient()
  );


  // Filtrer par sites sélectionnés
  const filteredServices = React.useMemo(() => {
    if (!services) return [];

    const sitesArray = Array.isArray(selectedSites) ? selectedSites : [selectedSites];


    if (!selectedSites || sitesArray.length === 0) {
      return services;
    }


    const selectedSiteIds = sitesArray.map(site => site.id);
    return services.filter(service => {
      return service.site && selectedSiteIds.includes(service.site.id);
    });
  }, [services, selectedSites]);




  const serviceOptions = React.useMemo(() => {
    if (!filteredServices || filteredServices.length === 0) return [];

    return filteredServices.map((service) => {
      const name = service.serviceName || service.buId?.toString() || 'Service sans nom';
      return {
        label: `${service.serviceId} - ${service.link_name} - ${name}`,
        value: service.serviceId || service.buId,
        data: service,
      };
    });
  }, [filteredServices]);


  // Réinitialiser le service sélectionné si les sites changent
  React.useEffect(() => {
    if (value && selectedSites) {
      const isServiceStillValid = serviceOptions.some(
        (option) => option.value === value?.value
      );

      // Si le service actuel n'est plus valide pour les nouveaux sites, réinitialiser
      if (!isServiceStillValid) {
        setFieldValue(name, null);
      }
    }
  }, [selectedSites, serviceOptions, value, name, setFieldValue]);


  React.useEffect(() => {
    if (defaultValue && serviceOptions.length > 0) {
      const defaultOption = serviceOptions.find(
        (option) => option.value?.toString() === defaultValue.toString()
      );
      if (defaultOption) {
        setFieldValue(name, defaultOption);
      }
    }
  }, [defaultValue, serviceOptions, name, setFieldValue]);


  const handleChange = (selectedOption: any) => {
    setFieldValue(name, selectedOption);
  };

  // Désactiver si aucun site n'est sélectionné
  const isSelectDisabled = isDisabled || !selectedSites;

  return (
    <Form.Group>
      <Form.Label className="fw-bold fs-3 form-label">
        {intl.formatMessage({ id: 'ADMINISTRATIVE.RECLAMATIONS.SERVICE' })}
      </Form.Label>
      <Select
        name={name}
        value={value}
        onChange={handleChange}
        options={serviceOptions}
        isDisabled={isSelectDisabled}
        isLoading={isLoading}
        isClearable
        placeholder={
          !selectedSites
            ? intl.formatMessage({ id: 'ADMINISTRATIVE.RECLAMATIONS.SERVICE.SELECT_SITE_FIRST' })
            : intl.formatMessage({ id: 'ADMINISTRATIVE.RECLAMATIONS.SERVICE.PLACEHOLDER' })
        }
        noOptionsMessage={() =>
          intl.formatMessage({ id: 'SELECT.NO.OPTIONS' })
        }
        className={touched && error ? 'is-invalid' : ''}
      />
      {touched && error && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default SelectService;
